import {
  applyTagAttributes,
  createTag,
  deleteTag,
  deleteTags,
  editTag,
  filterTag,
  showAddTagDialog,
  tagComponent,
  tagComponents,
  tagReference,
  untagComponent,
  untagReference,
  updateTag,
} from './TagActions';
import {
  collectRoutines,
  dispatchAction,
  routine,
  extractPayload,
  ofType,
  spreadMap,
} from '@ardoq/rxbeach';
import {
  tap,
  switchMap,
  mergeMap,
  filter,
  withLatestFrom,
} from 'rxjs/operators';
import { from, type Observable, type OperatorFunction } from 'rxjs';
import { tags$ } from './tags$';
import type { StreamCollection } from 'streams/utils/streamUtils';
import {
  isTruthy,
  isArdoqError,
  getArdoqErrorMessage,
  translateApiError,
  type ArdoqError,
  type Result,
} from '@ardoq/common-helpers';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { componentInterface } from '@ardoq/component-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import { confirmDeleteTag } from 'components/Dialogs/confirmDeletion/confirmDeletion';
import { globalFilters as Filters } from 'collections/filters';
import { createTagModal, editTagModal } from 'tabview/tagscape/tagEditor';
import { alert } from '@ardoq/modal';
import { logWarn } from '@ardoq/logging';
import {
  tagApi,
  scenarioApi,
  handleError,
  api,
  FALLBACK_ERROR_MESSAGE,
} from '@ardoq/api';
import {
  fetchTags,
  notifyFetchingTagsSucceeded,
  notifyFetchingTagsFailed,
  resetTagsWithData,
  upsertTags,
} from './actions';
import {
  ArdoqId,
  FilterTypes,
  APITagAttributes,
  UnpersistedEntity,
} from '@ardoq/api-types';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';

const getTagByName = (
  tags: StreamCollection<APITagAttributes>,
  tagName: string,
  workspaceId: ArdoqId
): APITagAttributes | undefined => {
  if (!tagName) return undefined;
  return tags.models
    .filter(tag => tag.rootWorkspace === workspaceId)
    .find(tag => tag.name.toLowerCase() === tagName.toLowerCase());
};

type TagCRUD = 'deleted' | 'created' | 'edited';

const getSubtitle = (i18nLocale: I18nLocale, operation: TagCRUD): string => {
  if (operation === 'created') {
    return t(
      i18nLocale,
      'tags:error.subtitle.created',
      "The tag wasn't created."
    );
  }
  if (operation === 'deleted') {
    return t(
      i18nLocale,
      'tags:error.subtitle.deleted',
      "The tag wasn't deleted."
    );
  }
  return t(i18nLocale, 'tags:error.subtitle.edited', "The tag wasn't saved.");
};

function localErrorHandler(operation: TagCRUD, error: ArdoqError): void {
  // i18nLocale$.state OK here: imperative one-shot alert; renders once, no re-render on locale change
  const i18nLocale = i18nLocale$.state;
  const fallbackMessage = t(
    i18nLocale,
    'tags:error.fallbackMessage',
    'Try again. If the problem continues, contact support.'
  );
  const translated = translateApiError(error, i18nLocale, fallbackMessage);
  alert({
    title: t(i18nLocale, 'tags:error.title', 'Error'),
    subtitle: getSubtitle(i18nLocale, operation),
    errorMessage:
      translated === FALLBACK_ERROR_MESSAGE ? fallbackMessage : translated,
  });
}

const alertOnError = <T>(operation: TagCRUD): OperatorFunction<Result<T>, T> =>
  handleError<T>(error => {
    api.logErrorIfNeeded(error);
    localErrorHandler(operation, error);
  });

const upsertPersistedTag = (
  result: Result<APITagAttributes>
): Result<APITagAttributes> => {
  if (!isArdoqError(result)) {
    dispatchAction(upsertTags([result]));
  }
  return result;
};

/**
 * Merge-only counterpart of [[upsertPersistedTag]] for updates: a response can
 * land after the tag has left the store (closing a scenario clears it), and
 * re-inserting it would leave a branch `_version` behind that shadows the real
 * mainline tag on the next load.
 */
const mergePersistedTag = (
  result: Result<APITagAttributes>
): Result<APITagAttributes> => {
  if (!isArdoqError(result)) {
    dispatchAction(applyTagAttributes(result));
  }
  return result;
};

const createTagAndInsertIntoStore = (
  attributes: UnpersistedEntity<APITagAttributes>,
  scenarioId: string | null
) => {
  if (scenarioId) {
    return scenarioApi
      .createTag(attributes, scenarioId)
      .then(upsertPersistedTag);
  }
  return tagApi.createTag(attributes).then(upsertPersistedTag);
};

const updateTagAndMergeIntoStore = (
  attributes: APITagAttributes,
  scenarioId: string | null
) => {
  if (scenarioId) {
    return scenarioApi
      .updateTag(attributes, scenarioId)
      .then(mergePersistedTag);
  }
  return tagApi.updateTag(attributes).then(mergePersistedTag);
};

const doDeleteTag = (tagId: string, scenarioId: string | null) => {
  if (scenarioId) {
    return scenarioApi.deleteTag(tagId, scenarioId);
  }
  return tagApi.deleteTag(tagId);
};

const handleDeleteTag = routine(
  ofType(deleteTag),
  extractPayload(),
  withLatestFrom(tags$),
  switchMap(([id, tags]) => {
    const tag = tags.byId[id];
    return confirmDeleteTag({
      tagName: tag?.name ?? '',
      componentNames: (tag?.components ?? [])
        .map(componentInterface.getDisplayName)
        .filter(isTruthy),
      referenceNames: (tag?.references ?? [])
        .map((refId: string) => {
          const ref = referenceInterface.get(refId);
          if (!ref) return null;
          const source =
            componentInterface.getFullPathName(ref.source) ?? 'Unknown';
          const target =
            componentInterface.getFullPathName(ref.target) ?? 'Unknown';
          return `${source} -> ${target}`;
        })
        .filter(isTruthy),
    }).then(confirmed => (confirmed ? id : null));
  }),
  filter(isTruthy),
  withLatestFrom(activeScenario$),
  switchMap(([id, { scenarioId }]) => doDeleteTag(id, scenarioId)),
  alertOnError('deleted')
);

const handleEditTag = routine(
  ofType(editTag),
  extractPayload(),
  withLatestFrom(tags$),
  tap(([id, tags]) => {
    const tag = tags.byId[id];
    if (tag) {
      editTagModal({ tag });
    }
  })
);

const handleFilterTag = routine(
  ofType(filterTag),
  extractPayload(),
  withLatestFrom(tags$),
  tap(([{ id, exclude }, tags]) => {
    const tagName = tags.byId[id]?.name;
    if (!tagName) return;
    Filters.createFilter(
      {
        isNegative: exclude,
        name: tagName,
        affectReference: true,
        affectComponent: true,
        type: FilterTypes.TAG,
        value: tagName,
      },
      { shouldTriggerChangeEvent: true }
    );
  })
);

const handleShowAddTagDialog = routine(
  ofType(showAddTagDialog),
  extractPayload(),
  tap(({ rootWorkspace }) => createTagModal(rootWorkspace))
);

const resolveTagIds = (
  { cids }: { cids: string[] },
  tags: StreamCollection<APITagAttributes>
): string[] => cids.map(cid => tags.byId[cid]?._id).filter(isTruthy);

const doDeleteTagsSequentially = async (
  ids: ArdoqId[],
  scenarioId: string | null
): Promise<Result<void>> => {
  const failures: ArdoqError[] = [];
  for (const id of ids) {
    const result = await doDeleteTag(id, scenarioId);
    if (isArdoqError(result)) failures.push(result);
  }
  if (failures.length > 0) return failures[0];
};

const handleDeleteTags = routine(
  ofType(deleteTags),
  extractPayload(),
  withLatestFrom(tags$),
  spreadMap(resolveTagIds),
  filter(ids => ids.length > 0),
  withLatestFrom(activeScenario$),
  switchMap(([ids, { scenarioId }]) =>
    doDeleteTagsSequentially(ids, scenarioId)
  ),
  alertOnError('deleted')
);

const toUntagComponentAttributes = (
  { tagCid, componentCid }: { tagCid: string; componentCid: string },
  tags: StreamCollection<APITagAttributes>
): APITagAttributes | null => {
  const tag = tags.byId[tagCid];
  if (!tag) return null;
  const component = componentInterface.get(componentCid);
  if (!component) {
    logWarn(Error('attempted to untag a nonexistent component'), null, {
      componentCid,
    });
    return null;
  }
  return {
    ...tag,
    components: (tag.components ?? []).filter(id => id !== component._id),
  };
};

const handleUntagComponent = routine(
  ofType(untagComponent),
  extractPayload(),
  withLatestFrom(tags$),
  spreadMap(toUntagComponentAttributes),
  filter(isTruthy),
  withLatestFrom(activeScenario$),
  switchMap(([attributes, { scenarioId }]) =>
    updateTagAndMergeIntoStore(attributes, scenarioId)
  ),
  alertOnError('edited')
);

const toUntagReferenceAttributes = (
  { tagCid, referenceCid }: { tagCid: string; referenceCid: string },
  tags: StreamCollection<APITagAttributes>
): APITagAttributes | null => {
  const tag = tags.byId[tagCid];
  if (!tag) return null;
  return {
    ...tag,
    references: (tag.references ?? []).filter(id => id !== referenceCid),
  };
};

const handleUntagReference = routine(
  ofType(untagReference),
  extractPayload(),
  withLatestFrom(tags$),
  spreadMap(toUntagReferenceAttributes),
  filter(isTruthy),
  withLatestFrom(activeScenario$),
  switchMap(([attributes, { scenarioId }]) =>
    updateTagAndMergeIntoStore(attributes, scenarioId)
  ),
  alertOnError('edited')
);

const handleCreateTag = routine(
  ofType(createTag),
  extractPayload(),
  withLatestFrom(activeScenario$),
  mergeMap(([attributes, { scenarioId }]) =>
    createTagAndInsertIntoStore(attributes, scenarioId)
  ),
  alertOnError('created')
);

const handleUpdateTag = routine(
  ofType(updateTag),
  extractPayload(),
  withLatestFrom(activeScenario$),
  mergeMap(([attributes, { scenarioId }]) =>
    updateTagAndMergeIntoStore(attributes, scenarioId)
  ),
  alertOnError('edited')
);

const toTagComponentsAttributes = (
  { tagId, componentIds }: { tagId: string | null; componentIds: Set<string> },
  tags: StreamCollection<APITagAttributes>
): APITagAttributes | null => {
  if (!tagId) return null;
  const tag = tags.byId[tagId];
  if (!tag) return null;
  const existing: string[] = tag.components ?? [];
  const merged = [...new Set([...existing, ...componentIds])];
  return { ...tag, components: merged };
};

const handleTagComponents = routine(
  ofType(tagComponents),
  extractPayload(),
  withLatestFrom(tags$),
  spreadMap(toTagComponentsAttributes),
  filter(isTruthy),
  withLatestFrom(activeScenario$),
  switchMap(([attributes, { scenarioId }]) =>
    updateTagAndMergeIntoStore(attributes, scenarioId)
  ),
  alertOnError('edited')
);

const handleFetchTags = routine(
  ofType(fetchTags),
  switchMap(tagApi.fetchAll),
  handleError(error => {
    dispatchAction(
      notifyFetchingTagsFailed({
        error: getArdoqErrorMessage(error),
      })
    );
    api.logErrorIfNeeded(error);
  }),
  tap(response => {
    dispatchAction(resetTagsWithData({ tags: response }));
    dispatchAction(notifyFetchingTagsSucceeded(response));
  })
);

type TagEntityPayload =
  | { attributes: UnpersistedEntity<APITagAttributes>; isNew: true }
  | { attributes: APITagAttributes; isNew: false };

const saveTagEntity = (
  payload: TagEntityPayload,
  scenarioId: string | null
): Observable<APITagAttributes> =>
  from(
    payload.isNew
      ? createTagAndInsertIntoStore(payload.attributes, scenarioId)
      : updateTagAndMergeIntoStore(payload.attributes, scenarioId)
  ).pipe(alertOnError(payload.isNew ? 'created' : 'edited'));

const toTagComponentPayload = (
  {
    tagName,
    componentId,
    rootWorkspaceId,
  }: {
    tagName: string;
    componentId: string;
    rootWorkspaceId: string;
  },
  tags: StreamCollection<APITagAttributes>
): TagEntityPayload | null => {
  const existing = getTagByName(tags, tagName, rootWorkspaceId);
  if (existing) {
    const components: string[] = existing.components ?? [];
    if (components.includes(componentId)) return null;
    return {
      attributes: {
        ...existing,
        components: [...components, componentId],
      },
      isNew: false,
    };
  }
  return {
    attributes: {
      name: tagName,
      description: null,
      rootWorkspace: rootWorkspaceId,
      components: [componentId],
      references: [],
    },
    isNew: true,
  };
};

const handleTagComponent = routine(
  ofType(tagComponent),
  extractPayload(),
  withLatestFrom(tags$),
  spreadMap(toTagComponentPayload),
  filter(isTruthy),
  withLatestFrom(activeScenario$),
  switchMap(([payload, { scenarioId }]) => saveTagEntity(payload, scenarioId))
);

const toTagReferencePayload = (
  {
    tagName,
    referenceId,
    rootWorkspaceId,
  }: {
    tagName: string;
    referenceId: string;
    rootWorkspaceId: string;
  },
  tags: StreamCollection<APITagAttributes>
): TagEntityPayload | null => {
  const existing = getTagByName(tags, tagName, rootWorkspaceId);
  if (existing) {
    const references: string[] = existing.references ?? [];
    if (references.includes(referenceId)) return null;
    return {
      attributes: {
        ...existing,
        references: [...references, referenceId],
      },
      isNew: false,
    };
  }
  return {
    attributes: {
      name: tagName,
      description: null,
      rootWorkspace: rootWorkspaceId,
      components: [],
      references: [referenceId],
    },
    isNew: true,
  };
};

const handleTagReference = routine(
  ofType(tagReference),
  extractPayload(),
  withLatestFrom(tags$),
  spreadMap(toTagReferencePayload),
  filter(isTruthy),
  withLatestFrom(activeScenario$),
  switchMap(([payload, { scenarioId }]) => saveTagEntity(payload, scenarioId))
);

export const routines = collectRoutines(
  handleShowAddTagDialog,
  handleDeleteTags,
  handleUntagComponent,
  handleUntagReference,
  handleTagComponents,
  handleDeleteTag,
  handleEditTag,
  handleFilterTag,
  handleUpdateTag,
  handleCreateTag,
  handleFetchTags,
  handleTagComponent,
  handleTagReference
);
